import { useRef, useState } from 'react';
import { Box } from '@mui/material';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import { useTranslation } from 'react-i18next';
import { SttButton } from '../../sporttia/buttons/SttButton';
import 'react-image-crop/dist/ReactCrop.css';

const DataReturnTypes = {
    blob: 'blob',
    base64: 'base64',
} as const;

type DataReturnType = (typeof DataReturnTypes)[keyof typeof DataReturnTypes];

interface ImageCropProps {
    src: string;
    cropWidth: number;
    cropHeight: number;
    targetWidth: number;
    targetHeight: number;
    onCrop?: (data: string | Blob) => void;
    dataReturnType: DataReturnType;
}

export default function ImageCrop({
    src,
    cropWidth,
    cropHeight,
    targetWidth,
    targetHeight,
    onCrop,
    dataReturnType,
}: ImageCropProps) {
    const { t } = useTranslation();

    const imageRef = useRef<HTMLImageElement>(null);
    const [crop, setCrop] = useState<Crop | undefined>();

    function getCroppedImg(
        image: HTMLImageElement,
        pixelCrop: PixelCrop,
        returnType: DataReturnType,
    ): string | Promise<Blob> {
        const canvas = document.createElement('canvas');
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            throw new Error('Unable to get 2D context from canvas');
        }

        ctx.drawImage(
            image,
            pixelCrop.x * scaleX,
            pixelCrop.y * scaleY,
            pixelCrop.width * scaleX,
            pixelCrop.height * scaleY,
            0,
            0,
            targetWidth,
            targetHeight,
        );

        if (returnType === DataReturnTypes.base64) {
            return canvas.toDataURL('image/jpeg', 1.0);
        }
        if (returnType === DataReturnTypes.blob) {
            return new Promise<Blob>((resolve: any, reject: any) => {
                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            reject(new Error('Canvas toBlob failed'));
                            return;
                        }
                        // Note: Blob doesn't have a name property, but we can create a File from it
                        resolve(blob);
                    },
                    'image/jpeg',
                    1,
                );
            });
        }
        throw new Error(
            'ImageCrop component requires a DataReturnType parameter',
        );
    }

    async function applyCrop(): Promise<void> {
        if (
            onCrop &&
            typeof onCrop === 'function' &&
            imageRef.current &&
            crop
        ) {
            const cropped = await getCroppedImg(
                imageRef.current,
                crop as PixelCrop,
                dataReturnType,
            );
            onCrop(cropped);
        }
    }

    function setup(): void {
        if (imageRef.current) {
            const { width, height } = imageRef.current;

            let adjustedCropWidth = cropWidth;
            let adjustedCropHeight = cropHeight;

            if (adjustedCropWidth > width) {
                adjustedCropWidth = width;
            }

            if (adjustedCropHeight > height) {
                adjustedCropHeight = height;
            }

            const rect: Crop = {
                unit: 'px',
                x: (width - adjustedCropWidth) / 2,
                y: (height - adjustedCropHeight) / 2,
                width: adjustedCropWidth,
                height: adjustedCropHeight,
            };

            setCrop(rect);
        }
    }

    return (
        <Box>
            <img
                ref={imageRef}
                src={src}
                alt=""
                style={{
                    visibility: 'hidden',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                }}
                onLoad={setup}
            />
            {crop && (
                <Box display="flex" flexDirection="column" alignItems="center">
                    <Box mb={3}>
                        <ReactCrop
                            crop={crop}
                            onChange={(newCrop) => setCrop(newCrop)}
                        >
                            <img src={src} alt="" />
                        </ReactCrop>
                    </Box>
                    <SttButton
                        caption={t('page.user.webcam.crop')}
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            applyCrop().catch((err: unknown) => {
                                console.error('Error applying crop:', err);
                            });
                        }}
                    />
                </Box>
            )}
        </Box>
    );
}
